import React from 'react';
import * as stylex from '@stylexjs/stylex';
import '../theme/Colors.stylex';
import { ColorNeutral } from '../theme/Colors.stylex';
import '../theme/Spaces.stylex';
import { Gap, Padding } from '../theme/Spaces.stylex';
import '../theme/Fonts.stylex';
import { FontFamily, FontSize, FontWeight } from '../theme/Fonts.stylex';
import { CanaryIconButton } from '../actions/CanaryIconButton';
import { Button } from '../actions/Button';
import { CanarySeparator } from './CanarySeparator';
import { Inline } from './Inline';
import { PredictiveLoadingBar, type Status as LoadingBarStatus } from '../feedback-indicators/PredictiveLoadingBar';
import { t } from 'src/primitives/I18n';

// MARK: SiblingNavigation
// =======================
type PageHeaderSiblingNavigationProps = {
  previousDisabled?: boolean;
  nextDisabled?: boolean;
  onRequestGoPrevious: () => void;
  onRequestGoNext: () => void;
};
function PageHeaderSiblingNavigationComponent({
  previousDisabled = false,
  nextDisabled = false,
  onRequestGoPrevious,
  onRequestGoNext
}: PageHeaderSiblingNavigationProps) {
  return <>
      <div className="display-x3nfvp2 alignItems-x6s0dn4 gap-x17d4w8g" data-style-src="wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:200">
        <Button icon="arrow_up" bold={false} size="small" tooltipText={t('Go to previous')} disabled={previousDisabled} onPress={onRequestGoPrevious} />
        <Button icon="arrow_down" bold={false} size="small" tooltipText={t('Go to next')} disabled={nextDisabled} onPress={onRequestGoNext} />
      </div>
      <CanarySeparator size="medium" spacing="xs" />
    </>;
}
const MemoizedPageHeaderSiblingNavigationComponent = React.memo(PageHeaderSiblingNavigationComponent);

// MARK: root
// ==========
type PageHeaderProps = {
  title: string;
  titlePlaceholder?: boolean;
  titlePrepender?: React.ReactElement;
  subtitle?: string;
  subtitleBadgeElement?: React.ReactElement;
  mainActionsElement?: React.ReactElement;
  siblingNavigationElement?: React.ReactElement;
  secondaryActionsElement?: React.ReactElement;
  actionsInert?: boolean;
  loadingBarStatus?: LoadingBarStatus;
  loadingBarEstimatedDuration?: number;
  scrolled?: boolean;
  onRequestGoBack?: () => void;
};
function PageHeaderComponent({
  title,
  titlePlaceholder = false,
  titlePrepender,
  subtitle,
  subtitleBadgeElement,
  mainActionsElement,
  secondaryActionsElement,
  siblingNavigationElement,
  actionsInert = false,
  loadingBarStatus,
  loadingBarEstimatedDuration,
  scrolled = false,
  onRequestGoBack
}: PageHeaderProps) {
  const hasSecondaryActions = secondaryActionsElement !== undefined;
  const hasMainActions = mainActionsElement !== undefined;
  const actionsInertProp = actionsInert ? {
    inert: ''
  } as Record<string, unknown> : {};
  return <header {...{
    0: {
      className: "position-x7wzq59 top-x13vifvy zIndex-x11uqc5h display-x78zum5 flexDirection-x1q0g3np alignItems-x6s0dn4 justifyContent-x1qughib gap-x7dls1a minHeight-x879a55 padding-x11aj2pl borderBottomWidth-xso031l borderBottomStyle-x1q0q8m5 borderBottomColor-x10m8i1d boxSizing-x9f619 backgroundColor-x12peec7",
      "data-style-src": "wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:168"
    },
    1: {
      className: "position-x7wzq59 top-x13vifvy zIndex-x11uqc5h display-x78zum5 flexDirection-x1q0g3np alignItems-x6s0dn4 justifyContent-x1qughib gap-x7dls1a minHeight-x879a55 padding-x11aj2pl borderBottomWidth-xso031l borderBottomStyle-x1q0q8m5 borderBottomColor-x10m8i1d boxSizing-x9f619 backgroundColor-x12peec7 boxShadow-xluy54d",
      "data-style-src": "wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:168; wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:185"
    }
  }[!!scrolled << 0]}>
      <div className="display-x78zum5 flexDirection-x1q0g3np alignItems-x6s0dn4 gap-x2u8dy9" data-style-src="wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:188">
        {onRequestGoBack && <>
            <CanaryIconButton iconName="arrow_left" iconWidth={17} iconHeight={17} size="small" onPress={onRequestGoBack} />
            <CanarySeparator size="small" spacing="none" />
          </>}
        {siblingNavigationElement}
        <Inline space="small">
          {titlePrepender && <div className="display-x78zum5 alignItems-x6s0dn4 justifyContent-xl56j7k" data-style-src="wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:206">{titlePrepender}</div>}
          <span {...{
          0: {
            className: "fontFamily-x111x8ze fontWeight-x5x31i3 fontSize-xv1t9fe lineHeight-xo5v014 color-x1w1nkkj whiteSpace-xuxw1ft",
            "data-style-src": "wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:211"
          },
          1: {
            className: "fontFamily-x111x8ze fontWeight-x5x31i3 fontSize-xv1t9fe lineHeight-xo5v014 whiteSpace-xuxw1ft color-x1j672if",
            "data-style-src": "wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:211; wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:219"
          }
        }[!!titlePlaceholder << 0]}>
            {title}
          </span>
        </Inline>
        {subtitle && <>
            <CanarySeparator size="small" spacing="none" />
            <Inline space="small">
              <span className="fontFamily-x111x8ze fontWeight-x1fkahbb fontSize-xv1t9fe lineHeight-xo5v014 color-xt4ixol whiteSpace-xuxw1ft" data-style-src="wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:222">{subtitle}</span>
              {subtitleBadgeElement}
            </Inline>
          </>}
      </div>
      <div {...actionsInertProp} className="display-x78zum5 flexDirection-x1q0g3np alignItems-x6s0dn4 gap-x2u8dy9" data-style-src="wino-gc:src/resources/layout-and-structure/CanaryPageHeader.tsx:194">
        {hasSecondaryActions && secondaryActionsElement}
        {hasSecondaryActions && hasMainActions && <CanarySeparator />}
        {hasMainActions && mainActionsElement}
      </div>
      {loadingBarStatus && <PredictiveLoadingBar status={loadingBarStatus} estimatedDuration={loadingBarEstimatedDuration} />}
    </header>;
}
const MemoizedPageHeaderComponent = React.memo(PageHeaderComponent);
export { MemoizedPageHeaderComponent as CanaryPageHeader, MemoizedPageHeaderSiblingNavigationComponent as CanaryPageHeaderSiblingNavigation };
export type { PageHeaderProps, PageHeaderSiblingNavigationProps };

// MARK: style
// ===========
const pageHeaderHeight = 60;
const pageHeaderContentMargin = 20;
export const pageHeaderContentOffset = pageHeaderHeight + pageHeaderContentMargin;